import type { TextNode } from '@repo/api-contracts';
import type { RenderMaterials } from '@repo/design-renderer';
import { type ReactElement, type RefObject, useEffect, useRef, useState } from 'react';
import { type FlatView, inlineTextStyleOf } from '@/features/design-editor';

interface InlineTextEditorProps {
  readonly node: TextNode;
  /** 노드 자리를 화면으로 펴는 보기 — 곡면은 노드 가운데의 국소 축척으로 편 근사 보기다. */
  readonly view: FlatView;
  readonly materials: Pick<RenderMaterials, 'fonts' | 'inks'>;
  /** 글자가 바뀔 때마다(한글 조합 중에는 조합이 끝난 뒤 한 번) 새 글자를 알린다. */
  readonly onInput: (text: string) => void;
  /** 고치기를 마쳤을 때 — Esc 면 'escape'(캔버스로 포커스를 돌린다), 바깥을 누르면 'blur'. */
  readonly onFinish: (reason: 'escape' | 'blur') => void;
  /**
   * 캔버스가 바깥 누름을 처리하기 전에 부르는 '지금 값 올리기' — 조합 중인 한글까지 지금 입력칸 값을 올린다.
   * 입력칸이 열려 있는 동안 여기에 함수를 걸어 둔다.
   */
  readonly flushRef?: RefObject<(() => void) | null>;
}

/**
 * 캔버스 위 글자 바로 고치기(마플식) — 노드와 같은 서체·크기·색·정렬·회전으로 노드 자리에 겹친 투명한 입력칸.
 * Enter 는 줄바꿈, Esc 나 바깥 클릭이면 마친다. 한글 조합 중에는 문서를 바꾸지 않고 조합이 끝나면 한 번 올린다.
 * 키·포인터 이벤트는 캔버스로 올려 보내지 않는다(화살표 이동·Delete·끌기와 겹치지 않게).
 */
export const InlineTextEditor = ({
  node,
  view,
  materials,
  onInput,
  onFinish,
  flushRef,
}: InlineTextEditorProps): ReactElement => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(node.text);
  const composing = useRef(false);
  const committed = useRef(node.text);
  const finished = useRef(false);
  /** 입력칸의 마지막 값(조합 중 글자 포함)과 최신 onInput — 닫히며 사라질 때 아직 안 올린 값을 올린다. */
  const latest = useRef(node.text);
  const onInputRef = useRef(onInput);
  onInputRef.current = onInput;

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    element.focus({ preventScroll: true });
    element.select();
  }, []);

  const commit = (text: string) => {
    if (text === committed.current) return;
    committed.current = text;
    onInputRef.current(text);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // 바깥 누름 등으로 blur·compositionend 가 오기 전에 사라져도 조합 중이던 글자를 잃지 않는다.
  useEffect(() => {
    const flush = () => commitRef.current(ref.current?.value ?? latest.current);
    if (flushRef !== undefined) flushRef.current = flush;
    return () => {
      if (flushRef !== undefined && flushRef.current === flush) flushRef.current = null;
      commitRef.current(latest.current);
    };
  }, [flushRef]);

  const finish = (reason: 'escape' | 'blur') => {
    if (finished.current) return;
    finished.current = true;
    onFinish(reason);
  };

  return (
    <textarea
      ref={ref}
      aria-label="글자 바로 고치기"
      data-testid="inline-text-editor"
      spellCheck={false}
      maxLength={1000}
      rows={1}
      className="absolute m-0 resize-none overflow-hidden border-0 bg-transparent p-0 whitespace-pre-wrap break-words outline-none"
      style={inlineTextStyleOf(node, view, materials)}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        latest.current = event.target.value;
        if (!composing.current) commit(event.target.value);
      }}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={(event) => {
        composing.current = false;
        commit(event.currentTarget.value);
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape' && !event.nativeEvent.isComposing) {
          event.preventDefault();
          commit(event.currentTarget.value);
          finish('escape');
        }
      }}
      onBlur={(event) => {
        commit(event.currentTarget.value);
        finish('blur');
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    />
  );
};
